import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import Select from "react-select";
import { apiURL } from "../config/config";
import { format } from "date-fns";
import quotationTypes from "../data/quotationTypes.json";

const CreateQuotationForm = ({
  quotationData,
  setQuotationData,
  handleChange,
  handleClientChange,
  errors = {},
}) => {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  const docDateRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // ✅ dark mode detection (รองรับตอนสลับ class "dark")
  const [isDark, setIsDark] = useState(() =>
    document?.documentElement?.classList?.contains("dark"),
  );

  useEffect(() => {
    const el = document?.documentElement;
    if (!el) return;

    const update = () => setIsDark(el.classList.contains("dark"));
    update();

    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });

    return () => obs.disconnect();
  }, []);

  // ดึงข้อมูลลูกค้า
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await axios.get(`${apiURL}clients`);
        setClients(response.data);
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, []);

  // ตั้งค่า createBy/proposedBy ตาม company
  useEffect(() => {
    if (quotationData.createBy && quotationData.proposedBy) return;

    const user = JSON.parse(localStorage.getItem("user")) || {};
    const email = user.username || "";
    const domain = email.split("@")[1]?.split(".")[0] || "";
    const companyName =
      domain === "neonworks" ? "NEON" : domain === "optx" ? "OPTX" : "UNKNOWN";

    if (companyName === "OPTX") {
      setQuotationData((prev) => ({
        ...prev,
        createBy: "CHAWACHAI WICHAIDIT ",
        proposedBy: "CHAWACHAI WICHAIDIT ",
      }));
    } else {
      setQuotationData((prev) => ({
        ...prev,
        createBy: "NATORN MANEEKORN",
        proposedBy: "AKEDANAI YUKTANAN",
      }));
    }
  }, [quotationData.createBy, quotationData.proposedBy, setQuotationData]);

  // โหลด type ของบริษัทจาก JSON
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const company = (user.company || "").toUpperCase();
    if (company && quotationTypes.companies[company]) {
      setAvailableTypes(
        Object.entries(quotationTypes.companies[company]).map(
          ([key, value]) => ({
            label: key, // "Creative"
            value: value, // "C"
          }),
        ),
      );
    } else {
      setAvailableTypes([]);
    }
  }, []);

  // sync title → projectName
  useEffect(() => {
    setQuotationData((prev) => ({ ...prev, projectName: prev.title }));
  }, [quotationData.title, setQuotationData]);

  // format period
  const formatPeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return "";
    const start = new Date(startDate);
    const end = new Date(endDate);
    const sameMonth =
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();
    return sameMonth
      ? format(start, "MMM yyyy")
      : `${format(start, "dd MMM yyyy")} - ${format(end, "dd MMM yyyy")}`;
  };

  const updateQuotationData = useCallback(
    (newData) => setQuotationData((prev) => ({ ...prev, ...newData })),
    [setQuotationData],
  );

  useEffect(() => {
    updateQuotationData({
      period: formatPeriod(quotationData.startDate, quotationData.endDate),
    });
  }, [quotationData.startDate, quotationData.endDate, updateQuotationData]);

  // ✅ react-select: options + selected value
  const clientOptions = useMemo(() => {
    return (clients || []).map((c) => ({
      value: c._id,
      label: c.customerName,
    }));
  }, [clients]);

  // รองรับทั้ง clientId และ client (ของเดิมในฟอร์มคุณใช้ clientId || client)
  const selectedClientId = quotationData.clientId || quotationData.client || "";
  const selectedClientOption = useMemo(() => {
    if (!selectedClientId) return null;
    return clientOptions.find((o) => o.value === selectedClientId) || null;
  }, [clientOptions, selectedClientId]);

  const openDatePicker = (ref) => {
    const input = ref.current;
    if (!input) return;

    input.focus();
    try {
      input.showPicker?.();
    } catch {
      // Some mobile browsers only allow the native picker from the direct tap.
    }
  };

  // ✅ styles ให้เข้ากับ Tailwind + dark mode
  const selectStyles = useMemo(() => {
    const bg = isDark ? "#1F2937" : "#FFFFFF"; // gray-800 / white
    const border = isDark ? "#374151" : "#D1D5DB"; // gray-700 / gray-300
    const errorBorder = isDark ? "#F87171" : "#EF4444"; // red-400 / red-500
    const text = isDark ? "#F9FAFB" : "#1F2937"; // gray-50 / gray-800
    const placeholder = isDark ? "#9CA3AF" : "#6B7280"; // gray-400 / gray-500
    const menuBg = isDark ? "#111827" : "#FFFFFF"; // gray-900 / white
    const hasError = Boolean(errors.clientId);

    return {
      control: (base, state) => ({
        ...base,
        backgroundColor: bg,
        borderColor: hasError
          ? errorBorder
          : state.isFocused
          ? "#60A5FA"
          : border,
        boxShadow: hasError
          ? "0 0 0 2px rgba(239, 68, 68, 0.25)"
          : state.isFocused
          ? "0 0 0 2px rgba(96,165,250,0.35)"
          : "none",
        fontSize: "16px",
        minHeight: "44px",
        borderRadius: "6px",
        ":hover": {
          borderColor: hasError
            ? errorBorder
            : state.isFocused
            ? "#60A5FA"
            : border,
        },
      }),
      singleValue: (base) => ({ ...base, color: text }),
      input: (base) => ({ ...base, color: text }),
      placeholder: (base) => ({ ...base, color: placeholder }),
      menu: (base) => ({
        ...base,
        backgroundColor: menuBg,
        borderRadius: "8px",
        overflow: "hidden",
        zIndex: 30,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "rgba(96,165,250,0.25)"
          : state.isFocused
            ? isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.05)"
            : "transparent",
        color: text,
        borderRadius: "6px",
        cursor: "pointer",
      }),
      noOptionsMessage: (base) => ({ ...base, color: placeholder }),
      loadingMessage: (base) => ({ ...base, color: placeholder }),
    };
  }, [isDark, errors.clientId]);

  const inputClass = (field, extra = "") =>
    `w-full min-w-0 max-w-full box-border min-h-[44px] px-3 py-2 border rounded-md bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-800 dark:text-gray-100 focus:ring-2 focus:outline-none ${
      errors[field]
        ? "border-red-500 dark:border-red-400 focus:ring-red-400"
        : "border-gray-300 dark:border-gray-700 focus:ring-blue-400"
    } ${extra}`;

  const ErrorText = ({ field }) =>
    errors[field] ? (
      <p className="mt-1 text-xs font-medium text-red-500 dark:text-red-400">
        {errors[field]}
      </p>
    ) : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm transition-colors duration-300 text-gray-800 dark:text-gray-200">
      {/* Title */}
      <div>
        <label className="block mb-1 text-gray-600 dark:text-gray-300">
          Title
        </label>
        <input
          type="text"
          name="title"
          placeholder="Enter title"
          value={quotationData.title}
          onChange={handleChange}
          className={inputClass("title")}
          required
        />
        <ErrorText field="title" />
      </div>

      {/* ✅ Client (react-select searchable) */}
      <div>
        <label className="block mb-1 text-gray-600 dark:text-gray-300">
          Client
        </label>

        <Select
          value={selectedClientOption}
          options={clientOptions}
          isClearable
          isSearchable
          isLoading={loadingClients}
          placeholder={loadingClients ? "Loading clients..." : "Select Client"}
          noOptionsMessage={() => "ไม่พบรายชื่อลูกค้า"}
          loadingMessage={() => "กำลังโหลดรายชื่อลูกค้า..."}
          styles={selectStyles}
          onChange={(opt) => {
            if (!opt) {
              handleClientChange("", "");
              return;
            }
            handleClientChange(opt.value, opt.label);
          }}
        />
        <ErrorText field="clientId" />
      </div>

      {/* Create By (แทน Sale Person) */}
      <div>
        <label className="block mb-1 text-gray-600 dark:text-gray-300">
          Create By
        </label>
        <input
          type="text"
          name="salePerson"
          value={quotationData.salePerson}
          onChange={handleChange}
          placeholder="Enter creator name"
          className={inputClass("salePerson")}
          required
        />
        <ErrorText field="salePerson" />
      </div>

      {/* Document Date */}
      <div>
        <label className="block mb-1 text-gray-600 dark:text-gray-300">
          Document Date
        </label>

        <div
          className="w-full"
          onClick={() => openDatePicker(docDateRef)}
          style={{ cursor: "pointer" }}
        >
          <input
            ref={docDateRef}
            type="date"
            name="documentDate"
            value={quotationData.documentDate}
            onChange={handleChange}
            onKeyDown={(e) => e.preventDefault()} // ห้ามพิมพ์
            className={inputClass("documentDate", "qt-date-input")}
          />
        </div>
        <ErrorText field="documentDate" />
      </div>

      {/* Product Name */}
      <div>
        <label className="block mb-1 text-gray-600 dark:text-gray-300">
          Product Name
        </label>
        <input
          type="text"
          name="productName"
          value={quotationData.productName}
          onChange={handleChange}
          placeholder="Enter product name"
          className={inputClass("productName")}
          required
        />
        <ErrorText field="productName" />
      </div>

      {/* Project Name */}
      <div>
        <label className="block mb-1 text-gray-600 dark:text-gray-300">
          Project Name
        </label>
        <input
          type="text"
          name="projectName"
          value={quotationData.projectName}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-700 text-base sm:text-sm text-gray-600 dark:text-gray-300"
          disabled
          readOnly
        />
      </div>

      {/* Period */}
      <div>
        <label className="block mb-1 text-gray-600 dark:text-gray-300">
          Period
        </label>
        <input
          type="text"
          name="period"
          value={quotationData.period}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-700 text-base sm:text-sm text-gray-600 dark:text-gray-300"
          disabled
        />
      </div>

      {/* Start / End Date */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:col-span-2">
        <div className="min-w-0">
          <label className="block mb-1 text-gray-600 dark:text-gray-300">
            Start Date
          </label>

          <div
            className="w-full"
            onClick={() => openDatePicker(startDateRef)}
            style={{ cursor: "pointer" }}
          >
            <input
              ref={startDateRef}
              type="date"
              name="startDate"
              value={quotationData.startDate}
              onChange={handleChange}
              onKeyDown={(e) => e.preventDefault()}
              className={inputClass("startDate", "qt-date-input")}
            />
          </div>
          <ErrorText field="startDate" />
        </div>

        <div className="min-w-0">
          <label className="block mb-1 text-gray-600 dark:text-gray-300">
            End Date
          </label>

          <div
            className="w-full"
            onClick={() => openDatePicker(endDateRef)}
            style={{ cursor: "pointer" }}
          >
            <input
              ref={endDateRef}
              type="date"
              name="endDate"
              value={quotationData.endDate}
              onChange={handleChange}
              onKeyDown={(e) => e.preventDefault()}
              className={inputClass("endDate", "qt-date-input")}
            />
          </div>
          <ErrorText field="endDate" />
        </div>
      </div>

      {/* Type + Fee */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:col-span-2">
        <div className="min-w-0">
          <label className="block mb-1 text-gray-600 dark:text-gray-300">
            Type
          </label>
          <select
            name="type"
            value={quotationData.type || ""}
            onChange={handleChange}
            className={inputClass("type")}
            required
          >
            <option value="">Select Type</option>
            {availableTypes.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <ErrorText field="type" />
        </div>

        {/* ✅ OPTX: Fee เป็น % ตามไฟล์เดิม */}
        <div className="min-w-0">
          <label className="block mb-1 text-gray-600 dark:text-gray-300">
            Fee (%)
          </label>
          <input
            type="number"
            name="fee"
            value={quotationData.fee}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              setQuotationData({
                ...quotationData,
                fee: value < 0 ? 0 : value,
              });
            }}
            className="w-full min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-400 focus:outline-none"
            required
          />
        </div>
      </div>

      {/* Create / Proposed By */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:col-span-2">
        <div className="min-w-0">
          <label className="block mb-1 text-gray-600 dark:text-gray-300">
            Proposed By
          </label>
          <input
            type="text"
            name="createBy"
            value={quotationData.createBy}
            className="w-full min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-700 text-base sm:text-sm text-gray-600 dark:text-gray-300"
            readOnly
          />
        </div>
        <div className="min-w-0">
          <label className="block mb-1 text-gray-600 dark:text-gray-300">
            Approved By
          </label>
          <input
            type="text"
            name="proposedBy"
            value={quotationData.proposedBy}
            className="w-full min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-700 text-base sm:text-sm text-gray-600 dark:text-gray-300"
            readOnly
          />
        </div>
      </div>

      {/* Discount + Credit Term */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:col-span-2">
        <div className="min-w-0">
          <label className="block mb-1 text-gray-600 dark:text-gray-300">
            Discount
          </label>
          <input
            type="number"
            name="discount"
            value={quotationData.discount || ""}
            onChange={handleChange}
            className="w-full min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
        </div>
        <div className="min-w-0">
          <label className="block mb-1 text-gray-600 dark:text-gray-300">
            Credit Term
          </label>
          <select
            name="CreditTerm"
            value={quotationData.CreditTerm ?? "30"}
            onChange={handleChange}
            className="w-full min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-400 focus:outline-none"
            required
          >
            <option value="0">See Remark</option>
            <option value="15">15 days</option>
            <option value="30">30 days</option>
            <option value="45">45 days</option>
          </select>
        </div>
      </div>

      {/* Remark */}
      <div className="md:col-span-2">
        <label className="block mb-1 text-gray-600 dark:text-gray-300">
          Remark
        </label>
        <textarea
          name="remark"
          value={quotationData.remark || ""}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-400 focus:outline-none"
          placeholder="Additional notes..."
        />
      </div>
    </div>
  );
};

export default CreateQuotationForm;
